import { isAcceptanceTest } from 'hub-http/helpers/env';
import Raven from 'raven-js';
import { ErrorInfo } from 'react';

Raven.setShouldSendCallback(() => !isAcceptanceTest());

export interface HubHttpError extends Error {
  status: number;
  responseText?: string;
  responseJSON?: Record<string, unknown>;
}
export interface RpcError extends Error {
  cause: HubHttpError;
}

export interface ChirpError extends Error {
  chirpErrorMessage: string;
  errorType: string;
}

export interface ChirpInternalError extends Error {
  type: string; // runtime value can be overwritten by spreadPropertiesIntoError (e.g. "UNKNOWN")
  chirpErrorType: string;
  message: string;
}

// errors thrown by hub-http add extra information to the error in hub-http#buildResponse
export const isHubHttpError = (error: Error): error is HubHttpError =>
  Number.isInteger((error as HubHttpError)?.status);

// errors thrown by client-types operations add the original hub-http error to the `error.cause` in createHttpRpcClientV2
export const isRpcError = (error: Error): error is RpcError =>
  !!error.cause && isHubHttpError(error.cause as HubHttpError);

// recursively looks for the root error by following the cause property until it finds the lowest level error
// this is useful for chirp errors which get nested 2 levels deep
export const getRootError = (error: Error): Error =>
  error.cause ? getRootError(error.cause as Error) : error;

// errors thrown by the chirp client when the BE returns a valid rpc error
export const isChirpError = (error: Error): error is ChirpError =>
  'chirpErrorMessage' in error && 'errorType' in error;

export const getChirpError = (error: Error): ChirpError | null => {
  const rootError = getRootError(error);
  if (isChirpError(rootError)) {
    return rootError;
  }
  return null;
};

// errors thrown by the chirp client when there is an unexpected BE internal error
export const isChirpInternalError = (
  error: Error
): error is ChirpInternalError =>
  'type' in error && (error as ChirpInternalError).type === 'INTERNAL_ERROR';

/**
 * Logs an error to Log Fetch with structured error information
 * @param customMessage - The error message by which the errors will be grouped together
 * @param error - The error object
 * @param uniqueId - The upgrade key or other unique identifier
 * @param fingerprint - Optional unique identifier to group similar errors together
 */
export const logRavenException = ({
  customMessage,
  error,
  uniqueId,
  fingerprint,
  boundaryName,
  info,
  usage,
  limit,
  seatType,
  tier,
  location,
}: {
  customMessage: string;
  error: any;
  uniqueId?: string;
  fingerprint?: string[];
  boundaryName?: string;
  info?: ErrorInfo;
  usage?: number;
  limit?: number;
  seatType?: string;
  tier?: string;
  location?: string;
}) => {
  if (isAcceptanceTest()) {
    return;
  }

  // 0 indicates user aborted requests; 401 indicates session timeout;
  // 5xx are transient server-side failures monitored by backend teams.
  // When the CHIRP gateway or an intermediate proxy (e.g. nginx) returns a
  // non-JSON HTTP error, hub-http throws a raw error whose .status is the HTTP
  // code but has no chirpErrorType. Suppressing the full 5xx range (not just
  // 500/502) prevents these from reaching Sentry.
  // CHIRP infrastructure errors: rpc-client-utils wraps all CHIRP failures in a
  // generic outer Error ("CHIRP RPC failed for {rpc}") whose .cause is a
  // ChirpInternalError. spreadPropertiesIntoError copies chirpErrorType from the
  // gateway response onto the cause; for routing failures this overwrites .type
  // so isChirpInternalError returns false, but chirpErrorType is still present.
  // Only known transient types are suppressed — user-defined service errors use
  // errorType (not chirpErrorType) and are unaffected.
  const isAbortedOrSessionTimeout = (status: unknown) =>
    status === 0 || status === 401;
  const isServerError = (status: unknown) =>
    typeof status === 'number' && status >= 500 && status < 600;
  const TRANSIENT_CHIRP_ERROR_TYPES = [
    'UNKNOWN_SERVER_ERROR',
    'SERVICE_TEMPORARILY_UNAVAILABLE',
    'TIMEOUT',
    'UNKNOWN', // gateway routing failures (e.g. UNKNOWN_SERVICE)
  ];
  if (
    isAbortedOrSessionTimeout(error?.status) ||
    isAbortedOrSessionTimeout(error?.cause?.status) ||
    isServerError(error?.status) ||
    isServerError(error?.cause?.status) ||
    (error?.cause instanceof Object && isChirpInternalError(error?.cause)) ||
    TRANSIENT_CHIRP_ERROR_TYPES.includes((error?.cause as any)?.chirpErrorType)
  )
    return;

  // CHIRP session timeout errors should not be logged
  const chirpError = getChirpError(error);
  if (chirpError?.chirpErrorMessage?.toLowerCase() === 'session timed out.') {
    return;
  }
  // isAuthError is set by rpc-client-utils when CHIRP gateway rejects request due to missing auth
  // credentials. This is equivalent to a session timeout and is not actionable from the frontend.
  if (error?.cause?.isAuthError === true) {
    return;
  }

  Raven.captureException(Error(customMessage, { cause: error }), {
    extra: {
      message: error?.message,
      uniqueId,
      boundaryName,
      info,
      usage,
      limit,
      seatType,
      tier,
      location,
    },
    fingerprint,
  });
};
